import React, { useMemo, useRef, useState, useEffect } from "react";
import PlanetIcon from "./PlanetIcon.jsx";

/* -------------------------------------------------------
   Tooltips (PM-aligned)
------------------------------------------------------- */
const TOOLTIP = {
  core: "The most consistent life themes this placement tends to create.",
  psych: "How this placement shapes emotions, mindset, and inner reactions.",
  karmic:
    "Where repetition, pressure, or lessons tend to arise — not punishment, but pattern.",
  lived: "Everyday situations where this placement often becomes visible.",
  integration:
    "A grounded way to work with the placement through awareness and balance.",
};

const isMobile = window.innerWidth < 640;

/* -------------------------------------------------------
   FIXED Tooltip Bubble (never clipped)
------------------------------------------------------- */
function InfoTip({ text, tipKey, openKey, setOpenKey }) {
  const ref = useRef(null);
  const isOpen = openKey === tipKey;

  const [pos, setPos] = useState({
    side: "right",
    top: 0,
    left: 0,
  });

  
  // Compute fixed position on open
  useEffect(() => {
    if (!isOpen) return;
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const tooltipWidth = 300;
    const gap = 10;

    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    const side =
      spaceRight >= tooltipWidth + gap
        ? "right"
        : spaceLeft >= tooltipWidth + gap
        ? "left"
        : "left";

    let top = rect.top + rect.height / 2;
    let left =
      side === "right" ? rect.right + gap : rect.left - tooltipWidth - gap;

    // clamp inside viewport
    left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));
    top = Math.max(60, Math.min(top, window.innerHeight - 60));

    setPos({ side, top, left });
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const onDocClick = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      setOpenKey(null);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen, setOpenKey]);

  return (
    <span
      ref={ref}
      onMouseEnter={() => setOpenKey(tipKey)}
      onMouseLeave={() => setOpenKey(null)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenKey((p) => (p === tipKey ? null : tipKey));
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 999,
        border: "1px solid rgba(148,163,184,0.35)",
        color: "#94a3b8",
        fontSize: 12,
        cursor: "help",
        userSelect: "none",
        flex: "0 0 auto",
      }}
    >
      ⓘ

      {isOpen ? (
        <span
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: 300,
            maxWidth: "min(300px, 72vw)",
            background: "rgba(248,250,252,0.98)",
            color: "#0f172a",
            border: "1px solid rgba(148,163,184,0.35)",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 12,
            lineHeight: 1.45,
            zIndex: 999999,
            boxShadow: "0 14px 40px rgba(0,0,0,0.45)",
            whiteSpace: "normal",
            pointerEvents: "none", // prevents hover flicker
          }}
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

/* -------------------------------------------------------
   Section block
   - Reduced spacing by ~20% (PM request)
   - Tooltip aligned right
   - Optional divider line
------------------------------------------------------- */
const Section = ({
  title,
  items,
  tooltip,
  openTooltipKey,
  setOpenTooltipKey,
  parentKey,
  isLast,
}) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  const tipKey = `${parentKey}-${title.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div
      style={{
        marginTop: 10,
        paddingBottom: isLast ? 0 : 10,
        borderBottom: isLast
          ? "none"
          : "1px dashed rgba(148,163,184,0.12)",
      }}
    >
      <div
        style={{
          fontWeight: 650,
          marginBottom: 5,
          fontSize: 13,
          color: "#93c5fd",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span>{title}</span>

        {tooltip ? (
          <InfoTip
            text={tooltip}
            tipKey={tipKey}
            openKey={openTooltipKey}
            setOpenKey={setOpenTooltipKey}
          />
        ) : null}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {items.map((t, i) => (
          <div
            key={i}
            style={{
              fontSize: 13,
              lineHeight: 1.55,
              color: "#e2e8f0",
              marginLeft: 10,
            }}
          >
            • {t}
          </div>
        ))}
      </div>
    </div>
  );
};

/* -------------------------------------------------------
   Main Component
------------------------------------------------------- */
/* -------------------------------------------------------
   Dasha role colours (shared between focus strip + badge)
------------------------------------------------------- */
const ROLE_COLOR = {
  MD: "#f97316",
  AD: "#22d3ee",
  PD: "#a855f7",
};
const ROLE_LABEL = {
  MD: "Mahadasha",
  AD: "Antardasha",
  PD: "Pratyantar",
};

export default function PlanetHouseEffects({ effects, dasha = null }) {
  // Accordion open state
  const [openKey, setOpenKey] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  // Tooltip state (only 1 open at a time)
  const [openTooltipKey, setOpenTooltipKey] = useState(null);

  // New UI states
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState("chart");

  const normalized = useMemo(() => {
    if (!Array.isArray(effects)) return [];

    return effects.map((info, idx) => {
      const key = `${info.planet}-${info.house}-${idx}`;

      const preview =
        (Array.isArray(info.core_experience) && info.core_experience[0]) ||
        (Array.isArray(info.psychological_experience) &&
          info.psychological_experience[0]) ||
        (Array.isArray(info.karmic_pressure) && info.karmic_pressure[0]) ||
        "";

      return { ...info, _key: key, _preview: preview };
    });
  }, [effects]);

  /* ── Active Dasha planet map: { Mercury: "MD", Venus: "AD", ... } ── */
  const activePlanetRoles = useMemo(() => {
    if (!dasha) return {};
    const map = {};
    if (dasha.md?.planet) map[dasha.md.planet] = "MD";
    if (dasha.ad?.planet) map[dasha.ad.planet] = "AD";
    if (dasha.pd?.planet) map[dasha.pd.planet] = "PD";
    return map;
  }, [dasha]);

  /* ── Focus entries: normalized entries whose planet is MD/AD/PD ── */
  const focusEntries = useMemo(() => {
    const roleOrder = { MD: 0, AD: 1, PD: 2 };
    return normalized
      .filter((e) => activePlanetRoles[e.planet])
      .sort(
        (a, b) =>
          roleOrder[activePlanetRoles[a.planet]] -
          roleOrder[activePlanetRoles[b.planet]]
      );
  }, [normalized, activePlanetRoles]);

  /* ── Jump to a planet card (open + scroll) ── */
  const jumpTo = (key) => {
    setOpenKey(key);
    setTimeout(() => {
      const el = document.getElementById(key);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  // Filter + sort (PM v1)
  const filtered = useMemo(() => {
    let arr = [...normalized];

    // 1) Filter search
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter((x) => {
        const s = `${x.planet} ${x.house}`.toLowerCase();
        return s.includes(q);
      });
    }

    // 2) Sort
    if (sortMode === "house") {
      arr.sort((a, b) => Number(a.house) - Number(b.house));
    } else if (sortMode === "planet") {
      arr.sort((a, b) => String(a.planet).localeCompare(String(b.planet)));
    }
    // chart = backend order, no change

    return arr;
  }, [normalized, query, sortMode]);

  // Defensive render
  if (!Array.isArray(effects) || effects.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "#9ca3af" }}>
        Planet-in-house insights are not available for this chart.
      </div>
    );
  }

  return (
    <div style={{ padding: 5 }}>
      {/* Title row + Collapse all */}
      {/* ================= TAB HEADER (Phase-7B Standard) ================= */}
      <div style={{ marginBottom: 14, marginTop: 10 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>🏠</span>

            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                letterSpacing: 0.2,
                color: "#fbbf24",
              }}
            >
              Planet in Houses
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setOpenKey(null);
              setOpenTooltipKey(null);
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,0.35)",
              background: "transparent",
              color: "#e5e7eb",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              flex: "0 0 auto",
            }}
          >
            Collapse all
          </button>
        </div>

        <div style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.6 }}>
          How planetary placements shape lived experience, psychological tone, and
          repeating karmic patterns — read as awareness, not prediction.
        </div>
      </div>

      {/* ── Active Dasha focus strip ── */}
      {focusEntries.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: "11px 14px",
            borderRadius: 10,
            background: "rgba(245,158,11,0.06)",
            border: "1px solid rgba(245,158,11,0.22)",
            borderLeft: "3px solid rgba(245,158,11,0.70)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(245,158,11,0.70)",
              marginBottom: 8,
            }}
          >
            Active in your current Dasha — start here
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {focusEntries.map((e) => {
              const role = activePlanetRoles[e.planet];
              const color = ROLE_COLOR[role];
              return (
                <button
                  key={e._key}
                  type="button"
                  onClick={() => jumpTo(e._key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 11px",
                    borderRadius: 999,
                    border: `1px solid ${color}55`,
                    background: `${color}14`,
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 12.5,
                    fontWeight: 500,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: color,
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: 700 }}>{e.planet}</span>
                  <span style={{ color: "#94a3b8" }}>· House {e.house}</span>
                  <span
                    style={{
                      fontSize: 10.5,
                      color,
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                    }}
                  >
                    · {ROLE_LABEL[role]}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 11.5,
              color: "rgba(148,163,184,0.55)",
            }}
          >
            Click any pill to jump to that planet's house placement details.
          </div>
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 220px",
          gap: 20,
          marginBottom: 14,
          alignItems: "start",
        }}
      >
        {/* Search */}
        <div style={{ position: "relative", width: "100%" }}>
          <input
            placeholder="Quick search by planet or house number…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 6px",
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.22)",
              background: "rgba(15,23,42,0.35)",
              color: "#e5e7eb",
              outline: "none",
              fontSize: 13,
            }}
          />

          {query.trim() ? (
            <button
              onClick={() => setQuery("")}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                border: "none",
                background: "transparent",
                color: "#94a3b8",
                fontSize: 16,
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
              }}
              aria-label="Clear search"
              title="Clear"
            >
              ✕
            </button>
          ) : null}
        </div>

        {/* Sort */}
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 16px",
            borderRadius: 12,
            border: "1px solid rgba(148,163,184,0.22)",
            background: "rgba(15,23,42,0.35)",
            color: "#e5e7eb",
            fontSize: 13,
            cursor: "pointer",
            outline: "none",
          }}
        >
          <option value="chart">Chart order</option>
          <option value="house">House order</option>
          <option value="planet">Planet name</option>
        </select>
      </div>

      {/* List */}
      {filtered.map((info) => {
        const isOpen = openKey === info._key;
        const isHovered = hoveredKey === info._key;

        const activeRole = activePlanetRoles[info.planet] || null;

        return (
          <div
            key={info._key}
            id={info._key}
            onMouseEnter={() => setHoveredKey(info._key)}
            onMouseLeave={() => setHoveredKey(null)}
            style={{
              marginBottom: 18,
              borderRadius: 14,
              background:
                isHovered || isOpen
                  ? "rgba(30,41,59,0.92)"
                  : "rgba(30,41,59,0.82)",
              border: isOpen
                ? "1px solid rgba(245,158,11,0.65)"
                : activeRole && !isHovered
                ? `1px solid ${ROLE_COLOR[activeRole]}45`
                : isHovered
                ? "1px solid rgba(148,163,184,0.45)"
                : "1px solid rgba(148,163,184,0.25)",
              boxShadow:
                isHovered
                  ? "0 10px 22px rgba(0,0,0,0.35)"
                  : "none",
              transition:
                "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
            }}
          >
            {/* Clickable Header */}
            <button
              onClick={() => {
                setOpenTooltipKey(null);
                setOpenKey(isOpen ? null : info._key);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: "14px 14px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              {/* Icon */}
              <div
                style={{
                  marginTop: 2,
                  fontSize: 18,
                  opacity: 1,
                  filter: "drop-shadow(0 0 4px rgba(0,0,0,0.4))",
                  flex: "0 0 auto"
                }}
              >
                <PlanetIcon planet={info.planet} size={18} />
              </div>

              {/* Title + preview */}
              <div style={{ flex: 1 }}>
                {/* New small label line */}
                <div
                  style={{
                    fontSize: 12,
                    color: "#94a3b8",
                    marginBottom: 2,
                    letterSpacing: 0.2,
                  }}
                >
                  Planet placement
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      color: "#f8fafc",
                      fontWeight: 750,
                    }}
                  >
                    {info.planet} in House {info.house}
                  </span>

                  {activeRole && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: 999,
                        background: `${ROLE_COLOR[activeRole]}20`,
                        border: `1px solid ${ROLE_COLOR[activeRole]}60`,
                        color: ROLE_COLOR[activeRole],
                        letterSpacing: "0.04em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ROLE_LABEL[activeRole]} — active now
                    </span>
                  )}
                </div>

                {!isOpen && info._preview ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#cbd5e1",
                      lineHeight: 1.45,
                      opacity: 0.95,
                    }}
                  >
                    {info._preview}
                  </div>
                ) : null}
              </div>

              {/* Chevron */}
              <div
                style={{
                  color: "#e2e8f0",
                  fontSize: 18,
                  marginTop: 1,
                  userSelect: "none",
                  opacity: 0.6,
                }}
              >
                {isOpen ? "▲" : "▼"}
              </div>
            </button>

            {/* Expanded content */}
            {isOpen ? (
              <div
                style={{
                  padding: "0 14px 14px",
                  borderTop: "1px solid rgba(148,163,184,0.14)",
                }}
              >
                <Section
                  parentKey={info._key}
                  title="Core Experience"
                  tooltip={TOOLTIP.core}
                  items={info.core_experience}
                  openTooltipKey={openTooltipKey}
                  setOpenTooltipKey={setOpenTooltipKey}
                  isLast={false}
                />

                <Section
                  parentKey={info._key}
                  title="Psychological Experience"
                  tooltip={TOOLTIP.psych}
                  items={info.psychological_experience}
                  openTooltipKey={openTooltipKey}
                  setOpenTooltipKey={setOpenTooltipKey}
                  isLast={false}
                />

                <Section
                  parentKey={info._key}
                  title="Karmic Pressure"
                  tooltip={TOOLTIP.karmic}
                  items={info.karmic_pressure}
                  openTooltipKey={openTooltipKey}
                  setOpenTooltipKey={setOpenTooltipKey}
                  isLast={false}
                />

                <Section
                  parentKey={info._key}
                  title="Lived Scenarios"
                  tooltip={TOOLTIP.lived}
                  items={info.lived_scenario}
                  openTooltipKey={openTooltipKey}
                  setOpenTooltipKey={setOpenTooltipKey}
                  isLast={false}
                />

                <Section
                  parentKey={info._key}
                  title="Integration Observation"
                  tooltip={TOOLTIP.integration}
                  items={info.integration_observation}
                  openTooltipKey={openTooltipKey}
                  setOpenTooltipKey={setOpenTooltipKey}
                  isLast={true}
                />
              </div>
            ) : null}
          </div>
        );
      })}

      {/* Empty state for filter */}
      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 10 }}>
          No planet placements match your search.
        </div>
      ) : null}
    </div>
  );
}
